import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Prompt, useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import ModeEditIcon from '@mui/icons-material/ModeEdit'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import Add from '@mui/icons-material/Add'
import ArrowUpward from '@mui/icons-material/ArrowUpward'
import ArrowDownward from '@mui/icons-material/ArrowDownward'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import FormControl from '@mui/material/FormControl'
// 날짜 선택 라이브러리
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import DesktopDatePicker from '@mui/lab/DesktopDatePicker'
import koLocale from 'date-fns/locale/ko'
import { format, parseISO } from 'date-fns'

import Title from '../../component/Title'
import ListTableToolbar, { LIST_TABLE_TOOLBAR_BUTTON_SX } from '../../component/ListTableToolbar'
import API from '../../component/Axios'
import ListActionGroup from '../../component/ListActionGroup'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import { FORM_DIALOG_ACTION_BUTTON_SX, FORM_DIALOG_DANGER_ACTION_BUTTON_SX, FORM_WIDE_UPLOAD_BUTTON_SX } from '../../component/BottomButtonGroup'
import { LIST_ACTION_DELETE_BUTTON_SX, LIST_ACTION_EDIT_BUTTON_SX } from '../../component/listActionIconStyles'
import { buildListSearch as buildListSearchQuery, createInitialListState, parseDateParam, parseNumberParam } from '../../utils/listPageState'
import {
    BANNER_V2_IMAGE_LEAVE_MESSAGE,
    hasPendingBannerV2Image,
    usePendingBannerV2ImageUnloadGuard,
} from './bannerV2LeaveGuard'

const locationOptions = [
    { value: 'all', label: '전체' },
    { value: 'top', label: '상단' },
    // 중간 배너는 현재 미사용
    // { value: 'middle', label: '중간' },
    { value: 'bottom', label: '하단' },
]
const formLocationOptions = locationOptions.filter((item) => item.value !== 'all')

const getDefaultBannerEndTime = () => {
    const d = new Date()
    d.setMonth(d.getMonth() + 1)
    return d
}

const serializeBannerDate = (value) => {
    if (!value) {
        return ''
    }

    const safeDate = value instanceof Date ? value : parseISO(String(value))
    return format(safeDate, 'yyyy-MM-dd')
}

const getLocationLabel = (value) => {
    // 목록에서 위치가 보이도록 라벨 매핑
    const locationMap = {
        top: '상단',
        // middle: '중간',
        bottom: '하단',
    }
    return locationMap[value] || '-'
}

const API_MAP = {
    top: {
        sort: 'sortUpdateBannerTop',
    },
    // middle: {
    //     sort: 'sortUpdateBannerMiddle',
    // },
    bottom: {
        sort: 'sortUpdateBannerBottom',
    },
}

const BANNER_V2_IMAGE_SIZE_BY_LOCATION = {
    top: { width: 890, height: 450, label: '상단' },
    bottom: { width: 1920, height: 490, label: '하단' },
}

const getBannerV2ImageSizeErrorMessage = (formLocation, imageSize) => {
    const requiredSize = BANNER_V2_IMAGE_SIZE_BY_LOCATION[formLocation]

    if (!requiredSize || !imageSize) {
        return ''
    }

    if (imageSize.width === requiredSize.width && imageSize.height === requiredSize.height) {
        return ''
    }

    return `${requiredSize.label} 배너 이미지는 ${requiredSize.width}x${requiredSize.height}px만 등록 가능합니다. 선택한 이미지: ${imageSize.width}x${imageSize.height}px`
}

// 다이얼로그 저장 전 필수값 검증 기준을 한 곳에서 관리한다.
const getBannerV2SubmitErrorMessage = ({
    editId,
    image,
    imageSize,
    formLocation,
    startTime,
    endTime,
}) => {
    if (!editId && !image) {
        return '이미지를 등록해주세요.'
    }

    if (image) {
        const imageSizeErrorMessage = getBannerV2ImageSizeErrorMessage(formLocation, imageSize)

        if (imageSizeErrorMessage) {
            return imageSizeErrorMessage
        }
    }

    if (!startTime || !endTime) {
        return '시작일과 종료일을 선택해주세요.'
    }

    if (startTime.getTime() > endTime.getTime()) {
        return '시작일은 종료일보다 이전이어야 합니다.'
    }

    return ''
}

const createBannerV2SubmitFormData = ({
    editId,
    image,
    linkUrl,
    startTime,
    endTime,
    formLocation,
    blankValue,
    statusValue,
}) => {
    const formData = new FormData()

    formData.append('url', linkUrl)
    formData.append('startTime', format(startTime, 'yyyy-MM-dd'))
    formData.append('endTime', format(endTime, 'yyyy-MM-dd'))
    formData.append('location', formLocation)
    formData.append('blank', blankValue)
    formData.append('status', statusValue)

    if (image) {
        formData.append('image', image)
    }

    if (editId) {
        formData.append('bannerV2Id', editId)
    }

    return {
        requestPath: editId ? 'updateBannerV2' : 'createBannerV2',
        formData,
    }
}

const BANNER_V2_IMAGE_FILE_ACCEPT = '.png,.jpg,.jpeg,image/png,image/jpeg'
const BANNER_V2_IMAGE_FILE_ERROR_MESSAGE = 'png, jpg, jpeg 파일만 업로드 가능합니다.'

const BANNER_V2_TABLE_COLUMNS = [
    { width: 120 },
    { width: 100 },
    { width: 110 },
    { width: 120 },
    { width: 340 },
    { width: 90 },
    { width: 90 },
    { width: 220 },
    { width: 120 },
]

const BANNER_V2_TABLE_MIN_WIDTH = BANNER_V2_TABLE_COLUMNS.reduce((sum, column) => sum + column.width, 0)

const BANNER_V2_LIST_IMAGE_SX = {
    width: 72,
    maxWidth: '100%',
    height: 44,
    objectFit: 'contain',
    border: '1px solid rgba(148, 163, 184, 0.25)',
    borderRadius: '4px',
    display: 'block',
}

const BANNER_V2_TEXT_CELL_SX = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}

const BANNER_V2_NOWRAP_CELL_SX = {
    whiteSpace: 'nowrap',
}

const isBannerV2ImageFile = (file) => {
    return Boolean(file) && (
        (file.type && ['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) ||
        /\.(png|jpe?g)$/i.test(file.name || '')
    )
}

const getBannerV2ImageDimensions = (file) => new Promise((resolve, reject) => {
    const previewUrl = URL.createObjectURL(file)
    const image = new Image()

    image.onload = () => {
        const dimensions = {
            width: image.naturalWidth,
            height: image.naturalHeight,
        }
        URL.revokeObjectURL(previewUrl)
        resolve(dimensions)
    }
    image.onerror = () => {
        URL.revokeObjectURL(previewUrl)
        reject(new Error('이미지 크기를 확인할 수 없습니다.'))
    }
    image.src = previewUrl
})

const revokeBannerV2PreviewUrl = (previewUrl) => {
    if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl)
    }
}

// 업로드한 이미지 preview URL은 교체 전에 정리해 브라우저 메모리 누수를 줄인다.
const applyBannerV2ImagePreview = ({
    file,
    currentPreviewUrl,
    setImage,
    setLocalImagePreviewUrl,
    setImagePreviewUrl,
}) => {
    revokeBannerV2PreviewUrl(currentPreviewUrl)

    const nextPreviewUrl = URL.createObjectURL(file)
    setImage(file)
    setLocalImagePreviewUrl(nextPreviewUrl)
    setImagePreviewUrl(nextPreviewUrl)
}

// 배너 폼의 기본 상태를 한 곳에서 만들어 초기화/복원 기준을 통일한다.
const createDefaultBannerV2DialogState = ({ selectedLocation }) => ({
    linkUrl: '',
    startTime: new Date(),
    endTime: getDefaultBannerEndTime(),
    image: null,
    imagePreviewUrl: null,
    localImagePreviewUrl: null,
    editId: null,
    formLocation: selectedLocation === 'all' ? 'top' : selectedLocation,
    blankValue: 0,
    statusValue: 1,
})

// 목록 아이템을 수정 폼 상태로 바꿀 때 사용하는 매핑 규칙을 묶어둔다.
const createBannerV2DialogStateFromItem = ({ item, selectedLocation }) => ({
    ...createDefaultBannerV2DialogState({ selectedLocation }),
    linkUrl: item.url || '',
    startTime: item.startTime ? parseISO(item.startTime) : new Date(),
    endTime: item.endTime ? new Date(item.endTime) : getDefaultBannerEndTime(),
    imagePreviewUrl: item.image ? `${process.env.REACT_APP_AWS_S3_URL}/${item.image}` : null,
    editId: item.bannerV2Id,
    formLocation: item.location === 'middle' ? 'top' : (item.location || selectedLocation),
    blankValue: typeof item.blank === 'number' ? item.blank : 0,
    statusValue: typeof item.status === 'number' ? item.status : 1,
})

// URL draft 값이 있으면 기본값/목록값 위에 덮어써 새로고침 후에도 편집 상태를 유지한다.
const createBannerV2DraftDialogState = ({ initialListState, selectedLocation, item = null }) => {
    const baseState = item
        ? createBannerV2DialogStateFromItem({ item, selectedLocation })
        : createDefaultBannerV2DialogState({ selectedLocation })

    return {
        ...baseState,
        linkUrl: initialListState.draftLinkUrl ?? baseState.linkUrl,
        startTime: initialListState.draftStartTime || baseState.startTime,
        endTime: initialListState.draftEndTime || baseState.endTime,
        formLocation: initialListState.draftFormLocation || baseState.formLocation,
        blankValue: initialListState.draftBlankValue ?? baseState.blankValue,
        statusValue: initialListState.draftStatusValue ?? baseState.statusValue,
    }
}

// setter 호출은 이 helper 하나로 모아 상태 필드 누락을 막는다.
const applyBannerV2DialogState = ({
    nextState,
    setLinkUrl,
    setStartTime,
    setEndTime,
    setImage,
    setImagePreviewUrl,
    setLocalImagePreviewUrl,
    setEditId,
    setFormLocation,
    setBlankValue,
    setStatusValue,
}) => {
    setLinkUrl(nextState.linkUrl)
    setStartTime(nextState.startTime)
    setEndTime(nextState.endTime)
    setImage(nextState.image)
    setImagePreviewUrl(nextState.imagePreviewUrl)
    setLocalImagePreviewUrl(nextState.localImagePreviewUrl)
    setEditId(nextState.editId)
    setFormLocation(nextState.formLocation)
    setBlankValue(nextState.blankValue)
    setStatusValue(nextState.statusValue)
}

// 성공/경고/오류 알림은 같은 형태로 열어 중복 setter 호출을 줄인다.
const openBannerV2Snackbar = ({
    severity,
    message,
    setOpenSnackbar,
    setSeveritySnackbar,
    setMessageSnackbar,
}) => {
    setOpenSnackbar(true)
    setSeveritySnackbar(severity)
    setMessageSnackbar(message)
}

const getBannerV2RequestErrorMessage = (err, fallbackMessage) => (
    err?.response?.data?.message || fallbackMessage
)

export default function BannerV2s({ adminId, role }) {
    const location = useLocation()
    const history = useHistory()
    const initialListStateRef = useRef(createInitialListState({
        location,
        getExtraState: ({ locationState, searchParams }) => {
            const dialogType = searchParams.get('dialog')

            return {
                selectedLocation: searchParams.get('location') || locationState.selectedLocation || 'all',
                createDialogOpen: dialogType === 'create',
                editDialogId: dialogType === 'edit' ? parseNumberParam(searchParams.get('bannerId'), null) : null,
                deleteDialogId: dialogType === 'delete' ? parseNumberParam(searchParams.get('bannerId'), null) : null,
                draftLinkUrl: searchParams.has('draftUrl') ? (searchParams.get('draftUrl') || '') : null,
                draftFormLocation: searchParams.has('formLocation') ? (searchParams.get('formLocation') || '') : null,
                draftBlankValue: searchParams.has('draftBlank') ? parseNumberParam(searchParams.get('draftBlank'), 0) : null,
                draftStatusValue: searchParams.has('draftStatus') ? parseNumberParam(searchParams.get('draftStatus'), 1) : null,
                draftStartTime: searchParams.has('draftStart') ? parseDateParam(searchParams.get('draftStart'), new Date()) : null,
                draftEndTime: searchParams.has('draftEnd') ? parseDateParam(searchParams.get('draftEnd'), getDefaultBannerEndTime()) : null,
            }
        },
    }))
    const initialListState = initialListStateRef.current
    const silentListRefreshRef = useRef(false)
    const dialogHydratedRef = useRef(false)
    const [loading, setLoading] = useState(true)
    const [selectedLocation, setSelectedLocation] = useState(initialListState.selectedLocation)
    const [totalCount, setTotalCount] = useState(0)
    const [params, setParams] = useState([])
    const [lastSort, setLastSort] = useState(0)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [listErrorMessage, setListErrorMessage] = useState('')
    const showSnackbar = useCallback((severity, message) => {
        openBannerV2Snackbar({
            severity,
            message,
            setOpenSnackbar,
            setSeveritySnackbar,
            setMessageSnackbar,
        })
    }, [])
    // 목록 조회 성공 시 count/rows/error 상태를 한 번에 반영한다.
    const applyListSuccessState = useCallback((responseData) => {
        setTotalCount(responseData.data.count || 0)
        setLastSort(responseData.data.lastSort || 0)
        setParams(responseData.data.rows || [])
        setListErrorMessage('')
    }, [])
    // 목록 조회 실패 시 기존 리스트를 비우고 오류 문구만 남긴다.
    const applyListErrorState = useCallback((errorMessage) => {
        setTotalCount(0)
        setLastSort(0)
        setParams([])
        setListErrorMessage(errorMessage)
    }, [])
    const handleApiResponse = useCallback(({
        data,
        onSuccess,
        onError,
        fallbackErrorMessage,
    }) => {
        if (data.code === 200) {
            onSuccess?.(data)
            return true
        }

        if (data.code === 403) {
            history.push('/login')
            return false
        }

        const errorMessage = data.message || fallbackErrorMessage
        if (onError) {
            onError(errorMessage)
            return false
        }

        showSnackbar('error', errorMessage)
        return false
    }, [history, showSnackbar])
    const formatBannerDate = (value) => {
        if (!value) {
            return '-'
        }
        try {
            return format(parseISO(value), 'yyyy-MM-dd')
        } catch (err) {
            return value
        }
    }
    
    // 현재 위치 기준으로 목록 조회
    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')
        const queryParams = {
            page: 1,
            offset: 10,
        }
        if (selectedLocation !== 'all') {
            queryParams.location = selectedLocation
        }

        try {
            const { data } = await API.post('/api/v1/admin/banner/getBannerListV2', {
                ...queryParams,
            })
            handleApiResponse({
                data,
                onSuccess: applyListSuccessState,
                onError: (errorMessage) => {
                    applyListErrorState(errorMessage)
                    showSnackbar('error', errorMessage)
                },
                fallbackErrorMessage: '배너 목록을 불러오지 못했습니다.',
            })
        } catch (err) {
            console.error(err)
            const errorMessage = getBannerV2RequestErrorMessage(err, '배너 목록을 불러오지 못했습니다.')
            applyListErrorState(errorMessage)
            showSnackbar('error', errorMessage)
        } finally {
            if (showSpinner) {
                setLoading(false)
            }
        }
    }, [applyListErrorState, applyListSuccessState, handleApiResponse, selectedLocation, showSnackbar])

    useEffect(() => {
        const showSpinner = !silentListRefreshRef.current
        silentListRefreshRef.current = false
        getData(showSpinner)
    }, [getData])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    // 위치 필터는 URL query와 동기화해 새로고침 후에도 유지한다.
    const handleLocationChange = (e) => {
        if (isSubmitting) {
            return
        }

        silentListRefreshRef.current = true
        setSelectedLocation(e.target.value)
    }

    // 노출순서 변경 (위치별 API 분기)
    const handleSortUpdate = async (bannerV2Id, sort, type) => {
        if (isSubmitting) return
        if (selectedLocation === 'all') {
            showSnackbar('warning', '전체 목록에서는 순서 변경할 수 없습니다. 위치를 선택해 주세요.')
            return
        }

        const apiName = API_MAP[selectedLocation].sort
        setIsSubmitting(true)

        const requestSortUpdate = async (targetSort) => {
            const { data } = await API.post(`/api/v1/admin/banner/${apiName}`, {
                bannerV2Id,
                sort: targetSort,
                type,
            })
            return data
        }

        const getLatestSort = async () => {
            const { data } = await API.post('/api/v1/admin/banner/getBannerListV2', {
                page: 1,
                offset: 100,
                ...(selectedLocation !== 'all' ? { location: selectedLocation } : {}),
            })

            if (data.code !== 200) return null

            const rows = data.data?.rows || []
            const target = rows.find((row) => row.bannerV2Id === bannerV2Id)
            return target ? target.sort : null
        }

        try {
            let data = await requestSortUpdate(sort)

            // 화면 캐시/동기화 지연으로 정렬값이 꼬였을 때 최신 sort를 다시 읽고 1회 재시도
            if (data.code === 400 && data.message === '배너 정렬값이 최신 상태가 아닙니다.') {
                const latestSort = await getLatestSort()
                if (latestSort && Number(latestSort) !== Number(sort)) {
                    data = await requestSortUpdate(latestSort)
                }
            }

            handleApiResponse({
                data,
                onSuccess: (responseData) => {
                    showSnackbar('success', responseData.message)
                    getData()
                },
                fallbackErrorMessage: '노출순서 변경 중 오류가 발생했습니다.',
            })
        } catch (err) {
            console.error(err)
            showSnackbar('error', getBannerV2RequestErrorMessage(err, '노출순서 변경 중 오류가 발생했습니다.'))
        } finally {
            setIsSubmitting(false)
        }
    }

    // 삭제 (통합 API; 삭제 전 MUI 다이얼로그로 2차 확인)
    const handleDelete = (id) => {
        setDeleteTargetId(id)
        setOpenDeleteDialog(true)
    }

    const [openDeleteDialog, setOpenDeleteDialog] = useState(Boolean(initialListState.deleteDialogId))
    const [deleteTargetId, setDeleteTargetId] = useState(initialListState.deleteDialogId)

    // 배너 삭제 확인 다이얼로그에서 삭제를 확정했을 때 실제 삭제 처리
    const executeDelete = async () => {
        if (isSubmitting) return
        if (!deleteTargetId) {
            return
        }

        setIsSubmitting(true)
        try {
            const { data } = await API.post('/api/v1/admin/banner/deleteBannerV2', { bannerV2Id: deleteTargetId })
            handleApiResponse({
                data,
                onSuccess: (responseData) => {
                    showSnackbar('success', responseData.message)
                    getData()
                },
                fallbackErrorMessage: '삭제 중 오류가 발생했습니다.',
            })
        } catch (err) {
            console.error(err)
            showSnackbar('error', getBannerV2RequestErrorMessage(err, '삭제 중 오류가 발생했습니다.'))
        } finally {
            setIsSubmitting(false)
            setOpenDeleteDialog(false)
            setDeleteTargetId(null)
        }
    }

    // 배너 삭제 취소
    const closeDeleteDialog = () => {
        if (isSubmitting) return
        setOpenDeleteDialog(false)
        setDeleteTargetId(null)
    }

    const [openDialog, setOpenDialog] = useState(Boolean(initialListState.createDialogOpen || initialListState.editDialogId))
    const [linkUrl, setLinkUrl] = useState(initialListState.draftLinkUrl ?? '')
    const [startTime, setStartTime] = useState(initialListState.draftStartTime || new Date())
    const [endTime, setEndTime] = useState(initialListState.draftEndTime || getDefaultBannerEndTime())
    const [image, setImage] = useState(null)
    const [imageSize, setImageSize] = useState(null)
    const [imagePreviewUrl, setImagePreviewUrl] = useState(null)
    const [localImagePreviewUrl, setLocalImagePreviewUrl] = useState(null)
    const [editId, setEditId] = useState(initialListState.editDialogId)
    // banner_v2 스키마 컬럼 매핑: 새창 여부(blank), 노출 여부(status)
    const [formLocation, setFormLocation] = useState(initialListState.draftFormLocation || 'top')
    const [blankValue, setBlankValue] = useState(initialListState.draftBlankValue ?? 0)
    const [statusValue, setStatusValue] = useState(initialListState.draftStatusValue ?? 1)
    const [navigationUnlocked, setNavigationUnlocked] = useState(false)
    const leaveGuardEnabled = openDialog && hasPendingBannerV2Image(image) && !isSubmitting && !navigationUnlocked

    usePendingBannerV2ImageUnloadGuard(leaveGuardEnabled)

    useEffect(() => {
        if (!dialogHydratedRef.current && (initialListState.createDialogOpen || initialListState.editDialogId || initialListState.deleteDialogId)) {
            return
        }

        const activeDialogType = openDialog
            ? (editId ? 'edit' : 'create')
            : openDeleteDialog && deleteTargetId
                ? 'delete'
                : ''

        const nextSearch = buildListSearchQuery({
            curPage: 1,
            searchQuery: '',
            extraParams: [
                {
                    param: 'location',
                    value: selectedLocation,
                    shouldInclude: (value) => value && value !== 'all',
                },
                {
                    param: 'dialog',
                    value: activeDialogType,
                    shouldInclude: () => Boolean(activeDialogType),
                },
                {
                    param: 'bannerId',
                    value: openDialog ? editId : deleteTargetId,
                    shouldInclude: (value) => Boolean(value) && (activeDialogType === 'edit' || activeDialogType === 'delete'),
                },
                {
                    param: 'draftUrl',
                    value: linkUrl,
                    shouldInclude: () => openDialog,
                },
                {
                    param: 'formLocation',
                    value: formLocation,
                    shouldInclude: () => openDialog && Boolean(formLocation),
                },
                {
                    param: 'draftStart',
                    value: startTime,
                    serialize: serializeBannerDate,
                    shouldInclude: () => openDialog && Boolean(startTime),
                },
                {
                    param: 'draftEnd',
                    value: endTime,
                    serialize: serializeBannerDate,
                    shouldInclude: () => openDialog && Boolean(endTime),
                },
                {
                    param: 'draftBlank',
                    value: blankValue,
                    shouldInclude: () => openDialog,
                },
                {
                    param: 'draftStatus',
                    value: statusValue,
                    shouldInclude: () => openDialog,
                },
            ],
        })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [blankValue, deleteTargetId, editId, endTime, formLocation, history, initialListState.createDialogOpen, initialListState.deleteDialogId, initialListState.editDialogId, linkUrl, location.pathname, location.search, openDeleteDialog, openDialog, selectedLocation, startTime, statusValue])

    // 폼 초기화
    const resetModalState = () => {
        revokeBannerV2PreviewUrl(localImagePreviewUrl)
        applyBannerV2DialogState({
            nextState: createDefaultBannerV2DialogState({ selectedLocation }),
            setLinkUrl,
            setStartTime,
            setEndTime,
            setImage,
            setImagePreviewUrl,
            setLocalImagePreviewUrl,
            setEditId,
            setFormLocation,
            setBlankValue,
            setStatusValue,
        })
        setImageSize(null)
        setOpenDialog(false)
    }

    const discardDialogChanges = () => {
        setNavigationUnlocked(true)
        resetModalState()
    }

    const closeDialog = (event) => {
        if (isSubmitting) return

        if (leaveGuardEnabled) {
            event?.preventDefault?.()

            if (!window.confirm(BANNER_V2_IMAGE_LEAVE_MESSAGE)) {
                return
            }
        }

        discardDialogChanges()
    }

    const handleOpenCreate = () => {
        setNavigationUnlocked(false)
        resetModalState()
        setOpenDialog(true)
    }

    const handleEdit = (item) => {
        setNavigationUnlocked(false)
        revokeBannerV2PreviewUrl(localImagePreviewUrl)
        applyBannerV2DialogState({
            nextState: createBannerV2DialogStateFromItem({ item, selectedLocation }),
            setLinkUrl,
            setStartTime,
            setEndTime,
            setImage,
            setImagePreviewUrl,
            setLocalImagePreviewUrl,
            setEditId,
            setFormLocation,
            setBlankValue,
            setStatusValue,
        })
        setImageSize(null)
        setOpenDialog(true)
    }

    const handleImageChange = async (e) => {
        const input = e.target
        const file = input.files?.[0]

        if (!file) {
            return
        }

        if (!isBannerV2ImageFile(file)) {
            input.value = ''
            showSnackbar('error', BANNER_V2_IMAGE_FILE_ERROR_MESSAGE)
            return
        }

        try {
            const nextImageSize = await getBannerV2ImageDimensions(file)
            const imageSizeErrorMessage = getBannerV2ImageSizeErrorMessage(formLocation, nextImageSize)

            if (imageSizeErrorMessage) {
                input.value = ''
                showSnackbar('error', imageSizeErrorMessage)
                return
            }

            setImageSize(nextImageSize)
            applyBannerV2ImagePreview({
                file,
                currentPreviewUrl: localImagePreviewUrl,
                setImage,
                setLocalImagePreviewUrl,
                setImagePreviewUrl,
            })
        } catch (err) {
            console.error(err)
            showSnackbar('error', '이미지 크기를 확인할 수 없습니다.')
        } finally {
            input.value = ''
        }
    }

    // 등록/수정 공통(선택 위치별 API 사용)
    const handleSubmit = async () => {
        if (isSubmitting) return
        const errorMessage = getBannerV2SubmitErrorMessage({
            editId,
            image,
            imageSize,
            formLocation,
            startTime,
            endTime,
        })

        if (errorMessage) {
            showSnackbar('warning', errorMessage)
            return
        }

        setIsSubmitting(true)
        try {
            const { requestPath, formData } = createBannerV2SubmitFormData({
                editId,
                image,
                linkUrl,
                startTime,
                endTime,
                formLocation,
                blankValue,
                statusValue,
            })

            const { data } = await API.post(`/api/v1/admin/banner/${requestPath}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })

            handleApiResponse({
                data,
                onSuccess: () => {
                    resetModalState()
                    showSnackbar('success', editId ? '수정되었습니다.' : '등록되었습니다.')
                    getData()
                },
                fallbackErrorMessage: '등록 중 오류가 발생했습니다.',
            })
        } catch (err) {
            console.error(err)
            showSnackbar('error', getBannerV2RequestErrorMessage(err, '등록 중 오류가 발생했습니다.'))
        } finally {
            setIsSubmitting(false)
        }
    }

    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="배너 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            onClick={() => getData(false)}
                            disabled={isSubmitting}
                        >
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (selectedLocation !== 'all') {
            return (
                <ListEmptyState
                    title={`${locationOptions.find((item) => item.value === selectedLocation)?.label || '선택한 위치'} 배너가 없습니다.`}
                    description="전체 위치로 변경하거나 신규 배너를 추가해보세요."
                    action={(
                        <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            onClick={() => setSelectedLocation('all')}
                            disabled={isSubmitting}
                        >
                            전체 보기
                        </Button>
                    )}
                />
            )
        }

        return (
            <ListEmptyState
                title="등록된 배너가 없습니다."
                description="상단 또는 하단 위치를 선택해 신규 배너를 추가할 수 있습니다."
            />
        )
    }

    // 새로고침으로 복귀했을 때 현재 목록 데이터에서 다시 찾아 등록/수정/삭제 다이얼로그를 연다.
    useEffect(() => {
        if (loading || dialogHydratedRef.current) {
            return
        }

        if (initialListState.createDialogOpen) {
            dialogHydratedRef.current = true
            applyBannerV2DialogState({
                nextState: createBannerV2DraftDialogState({ initialListState, selectedLocation }),
                setLinkUrl,
                setStartTime,
                setEndTime,
                setImage,
                setImagePreviewUrl,
                setLocalImagePreviewUrl,
                setEditId,
                setFormLocation,
                setBlankValue,
                setStatusValue,
            })
            setOpenDialog(true)
            return
        }

        if (initialListState.editDialogId) {
            const targetItem = params.find((item) => Number(item.bannerV2Id) === Number(initialListState.editDialogId))

            dialogHydratedRef.current = true

            if (!targetItem) {
                applyBannerV2DialogState({
                    nextState: createBannerV2DraftDialogState({ initialListState, selectedLocation }),
                    setLinkUrl,
                    setStartTime,
                    setEndTime,
                    setImage,
                    setImagePreviewUrl,
                    setLocalImagePreviewUrl,
                    setEditId,
                    setFormLocation,
                    setBlankValue,
                    setStatusValue,
                })
                setOpenDialog(false)
                return
            }

            applyBannerV2DialogState({
                nextState: createBannerV2DraftDialogState({ initialListState, selectedLocation, item: targetItem }),
                setLinkUrl,
                setStartTime,
                setEndTime,
                setImage,
                setImagePreviewUrl,
                setLocalImagePreviewUrl,
                setEditId,
                setFormLocation,
                setBlankValue,
                setStatusValue,
            })
            setOpenDialog(true)
            return
        }

        if (initialListState.deleteDialogId) {
            const targetItem = params.find((item) => Number(item.bannerV2Id) === Number(initialListState.deleteDialogId))

            dialogHydratedRef.current = true

            if (!targetItem) {
                setOpenDeleteDialog(false)
                setDeleteTargetId(null)
                return
            }

            setDeleteTargetId(targetItem.bannerV2Id)
            setOpenDeleteDialog(true)
        }
    }, [initialListState, initialListState.createDialogOpen, initialListState.deleteDialogId, initialListState.draftBlankValue, initialListState.draftEndTime, initialListState.draftFormLocation, initialListState.draftLinkUrl, initialListState.draftStartTime, initialListState.draftStatusValue, initialListState.editDialogId, loading, params, selectedLocation])

    useEffect(() => {
        return () => {
            revokeBannerV2PreviewUrl(localImagePreviewUrl)
        }
    }, [localImagePreviewUrl])

    return (
        <Grid item xs={12}>
            <Prompt when={leaveGuardEnabled} message={BANNER_V2_IMAGE_LEAVE_MESSAGE} />
            {loading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 5, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Title>배너 관리 (v2)</Title>
                    <ListTableToolbar
                        left={(
                            <TextField
                                select
                                size="small"
                                label="배너 위치"
                                value={selectedLocation}
                                onChange={handleLocationChange}
                                disabled={isSubmitting}
                                sx={{ minWidth: 160 }}
                            >
                                {locationOptions.map((item) => (
                                    <MenuItem key={item.value} value={item.value}>
                                        {item.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                        right={(selectedLocation === 'all' || totalCount < 10) && (
                            <Button
                                onClick={handleOpenCreate}
                                variant="contained"
                                color="success"
                                startIcon={<Add />}
                                size="small"
                                disabled={isSubmitting}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                            >
                                신규추가
                            </Button>
                        )}
                    />
                    <ListTableContainer>
                        <Table size="small" sx={{ minWidth: BANNER_V2_TABLE_MIN_WIDTH, tableLayout: 'fixed' }}>
                            <colgroup>
                                {BANNER_V2_TABLE_COLUMNS.map((column, index) => (
                                    <col key={`banner-v2-column-${index}`} style={{ width: column.width }} />
                                ))}
                            </colgroup>
                            <caption>Total Count: {totalCount}</caption>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>배너 위치</TableCell>
                                        <TableCell>노출순서</TableCell>
                                        <TableCell>이미지</TableCell>
                                        <TableCell>순서변경</TableCell>
                                        <TableCell align="center">linkUrl</TableCell>
                                        <TableCell align="center">새창</TableCell>
                                        <TableCell align="center">노출</TableCell>
                                        <TableCell>노출기간</TableCell>
                                        <TableCell align="right">Action</TableCell>
                                    </TableRow>
                                </TableHead>
                            <TableBody>
                                {params.length > 0 ? (
                                    params.map((row) => (
                                        <TableRow key={row.bannerV2Id}>
                                            <TableCell sx={BANNER_V2_NOWRAP_CELL_SX}>{getLocationLabel(row.location)}</TableCell>
                                            <TableCell sx={BANNER_V2_NOWRAP_CELL_SX}>{row.sort}</TableCell>
                                            <TableCell>
                                                {row.image ? (
                                                    <img
                                                        src={`${process.env.REACT_APP_AWS_S3_URL}/${row.image}`}
                                                        alt=""
                                                        style={BANNER_V2_LIST_IMAGE_SX}
                                                    />
                                                ) : '-'}
                                            </TableCell>
                                            <TableCell sx={BANNER_V2_NOWRAP_CELL_SX}>
                                            {selectedLocation !== 'all' && row.sort > 1 && <IconButton onClick={() => handleSortUpdate(row.bannerV2Id, row.sort, 'up')} disabled={isSubmitting}><ArrowUpward /></IconButton>}
                                            {selectedLocation !== 'all' && lastSort !== row.sort && <IconButton onClick={() => handleSortUpdate(row.bannerV2Id, row.sort, 'down')} disabled={isSubmitting}><ArrowDownward /></IconButton>}
                                            </TableCell>
                                            <TableCell align="center" sx={BANNER_V2_TEXT_CELL_SX} title={row.url || ''}>{row.url || '-'}</TableCell>
                                            <TableCell align="center" sx={BANNER_V2_NOWRAP_CELL_SX}>{row.blank === 1 ? '새창' : '현재창'}</TableCell>
                                            <TableCell align="center" sx={BANNER_V2_NOWRAP_CELL_SX}>{row.status === 1 ? '노출' : '미노출'}</TableCell>
                                            <TableCell sx={BANNER_V2_NOWRAP_CELL_SX}>{`${formatBannerDate(row.startTime)} ~ ${formatBannerDate(row.endTime)}`}</TableCell>
                                            <TableCell align="right" sx={BANNER_V2_NOWRAP_CELL_SX}>
                                                <ListActionGroup disabled={isSubmitting}>
                                                    <Tooltip title="Edit">
                                                        <Box
                                                            component="button"
                                                            type="button"
                                                            onClick={() => handleEdit(row)}
                                                            aria-label="Edit"
                                                            sx={{
                                                                ...LIST_ACTION_EDIT_BUTTON_SX,
                                                                cursor: isSubmitting ? 'default' : 'pointer',
                                                            }}
                                                        >
                                                            <ModeEditIcon />
                                                        </Box>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <Box
                                                            component="button"
                                                            type="button"
                                                            onClick={() => handleDelete(row.bannerV2Id)}
                                                            aria-label="Delete"
                                                            sx={{
                                                                ...LIST_ACTION_DELETE_BUTTON_SX,
                                                                cursor: isSubmitting ? 'default' : 'pointer',
                                                            }}
                                                        >
                                                            <DeleteOutline />
                                                        </Box>
                                                    </Tooltip>
                                                </ListActionGroup>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={9}>
                                            {renderEmptyState()}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </ListTableContainer>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}

                    <Dialog open={openDialog} onClose={closeDialog} maxWidth="sm" fullWidth>
                        <DialogTitle>배너 {editId ? '수정' : '등록'} ({locationOptions.find((item) => item.value === formLocation)?.label || '위치 미선택'})</DialogTitle>
                        <DialogContent>
                            <Box display="flex" flexDirection="column" gap={2} mt={1}>
                                <TextField
                                    select
                                    size="small"
                                    label="배너 위치"
                                    value={formLocation}
                                    onChange={(e) => setFormLocation(e.target.value)}
                                    // 배너 저장 API에서 사용 가능한 위치만 노출(top/bottom)
                                    // 배너 수정 시에도 위치 변경 허용
                                    disabled={isSubmitting}
                                    sx={{ minWidth: 140 }}
                                >
                                    {formLocationOptions.map((item) => (
                                        <MenuItem key={item.value} value={item.value}>
                                            {item.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    select
                                    size="small"
                                    label="새창 여부"
                                    value={blankValue}
                                    onChange={(e) => setBlankValue(Number(e.target.value))}
                                    disabled={isSubmitting}
                                    sx={{ minWidth: 140 }}
                                >
                                    <MenuItem value={0}>현재창</MenuItem>
                                    <MenuItem value={1}>새창</MenuItem>
                                </TextField>
                                <TextField
                                    select
                                    size="small"
                                    label="노출 여부"
                                    value={statusValue}
                                    onChange={(e) => setStatusValue(Number(e.target.value))}
                                    disabled={isSubmitting}
                                    sx={{ minWidth: 140 }}
                                >
                                    <MenuItem value={1}>노출</MenuItem>
                                    <MenuItem value={0}>미노출</MenuItem>
                                </TextField>
                                <TextField
                                    label="url"
                                    variant="outlined"
                                    value={linkUrl}
                                    onChange={(e) => setLinkUrl(e.target.value)}
                                    disabled={isSubmitting}
                                    fullWidth
                                />
                                <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6} md={6} sm={12}>
                                            <FormControl fullWidth>
                                                <DesktopDatePicker
                                                    required
                                                    inputFormat="yyyy-MM-dd"
                                                    mask={"____-__-__"}
                                                    label="시작일"
                                                    value={startTime}
                                                    onChange={date => setStartTime(date)}
                                                    disabled={isSubmitting}
                                                    renderInput={(params) => <TextField required {...params} size="small" disabled={isSubmitting} />}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={6} md={6} sm={12}>
                                            <FormControl fullWidth>
                                                <DesktopDatePicker
                                                    inputFormat="yyyy-MM-dd"
                                                    mask={"____-__-__"}
                                                    label="종료일"
                                                    value={endTime}
                                                    minDate={startTime}
                                                    onChange={date => setEndTime(date)}
                                                    disabled={isSubmitting}
                                                    renderInput={(params) => <TextField required {...params} size="small" disabled={isSubmitting} />}
                                                />
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </LocalizationProvider>
                                <FormControl fullWidth>
                                    <TextField
                                        inputProps={{ accept: BANNER_V2_IMAGE_FILE_ACCEPT }}
                                        style={{ display: 'none' }}
                                        name="image"
                                        id="bannerV2Image"
                                        type="file"
                                        disabled={isSubmitting}
                                        onChange={handleImageChange}
                                    />
                                    <Box component="label" htmlFor="bannerV2Image" sx={{ display: 'block' }}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="success"
                                            component="span"
                                            startIcon={<Add />}
                                            sx={FORM_WIDE_UPLOAD_BUTTON_SX}
                                            disabled={isSubmitting}
                                        >
                                            이미지 선택
                                        </Button>
                                    </Box>
                                    <Box component="p" sx={{ mt: 1, mb: 0, fontSize: '0.75rem', color: 'text.secondary' }}>
                                        상단 890x450px, 하단 1920x490px 이미지만 등록 가능합니다.
                                    </Box>
                                </FormControl>
                                {image && (
                                    <Box component="p" sx={{ mt: 1, mb: 0, fontSize: '0.75rem', color: 'text.secondary' }}>
                                        선택한 파일: {image.name}
                                    </Box>
                                )}
                                {imagePreviewUrl && (
                                    <Box
                                        sx={{
                                            mt: 2,
                                            width: '100%',
                                            height: { xs: 220, md: 300 },
                                            borderRadius: '4px',
                                            border: '1px solid rgba(148, 163, 184, 0.24)',
                                            backgroundColor: '#f8fafc',
                                            overflow: 'hidden',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={imagePreviewUrl}
                                            alt="미리보기"
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain',
                                                display: 'block',
                                            }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeDialog} variant="outlined" color="secondary" sx={FORM_DIALOG_ACTION_BUTTON_SX} disabled={isSubmitting}>취소</Button>
                            <Button onClick={handleSubmit} variant="contained" color="primary" sx={FORM_DIALOG_ACTION_BUTTON_SX} disabled={isSubmitting}>저장</Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog
                        open={openDeleteDialog}
                        onClose={closeDeleteDialog}
                        aria-labelledby="banner-delete-title"
                        aria-describedby="banner-delete-description"
                    >
                        <DialogTitle id="banner-delete-title">배너 삭제</DialogTitle>
                        <DialogContent>
                            <DialogContentText id="banner-delete-description">
                                선택한 배너를 삭제하시겠습니까? 삭제한 배너는 복구할 수 없습니다.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeDeleteDialog} variant="outlined" color="secondary" sx={FORM_DIALOG_ACTION_BUTTON_SX} disabled={isSubmitting}>취소</Button>
                            <Button onClick={executeDelete} variant="contained" color="error" sx={FORM_DIALOG_DANGER_ACTION_BUTTON_SX} disabled={isSubmitting}>삭제</Button>
                        </DialogActions>
                    </Dialog>
                </Paper>
            )}
        </Grid>
    )
}
